/**
 * Runtime quota feature identity for GET /v1/tutor/runtime/quota.
 *
 * API/query identity stays distinct:
 * - `ai_lesson_tutor` — Tamil Tutor / lesson live experience
 * - `live_ai_tutor` — sidebar pooled "All live tutors" resource
 *
 * Backend accounting (live pool on) maps `live_ai_tutor` → `ai_lesson_tutor`
 * for school allocation, ledger, and active-session lookup. Do NOT merge
 * these into one TanStack query key.
 */

export const LIVE_POOL_LEGACY_DISPLAY_FEATURES = [
  'ai_lesson_tutor',
  'communication_tutor',
  'scenario_conversation_tutor',
  'picture_talk_tutor',
] as const;

export type LivePoolLegacyDisplayFeature = (typeof LIVE_POOL_LEGACY_DISPLAY_FEATURES)[number];

export const POOL_FEATURE_NAME = 'live_ai_tutor';

/** Exact API `feature_name` query parameter (no alias normalization). */
export function runtimeQuotaApiFeatureName(featureName: string): string {
  const trimmed = String(featureName || '').trim();
  return trimmed || 'ai_lesson_tutor';
}

export function isLivePoolLegacyDisplayFeature(
  featureName: string,
): featureName is LivePoolLegacyDisplayFeature {
  return (LIVE_POOL_LEGACY_DISPLAY_FEATURES as readonly string[]).includes(
    runtimeQuotaApiFeatureName(featureName),
  );
}

export type RuntimeQuotaFeatureContractRow = {
  featureName: string;
  frontendMeaning: string;
  backendMeaning: string;
  sameQuotaPool: boolean;
  safeToNormalizeWith: string | null;
};

/** Documented feature matrix for audits (not used to merge query keys). */
export function runtimeQuotaFeatureContractMatrix(): RuntimeQuotaFeatureContractRow[] {
  return [
    {
      featureName: 'ai_lesson_tutor',
      frontendMeaning: 'Tamil Tutor / lesson live experience sidebar scope',
      backendMeaning: 'Display feature; pooled monthly usage when live pool enabled',
      sameQuotaPool: true,
      safeToNormalizeWith: null,
    },
    {
      featureName: 'live_ai_tutor',
      frontendMeaning: 'Sidebar pooled "All live tutors" scope',
      backendMeaning: 'Pool subscription feature key (POOL_FEATURE_NAME)',
      sameQuotaPool: true,
      safeToNormalizeWith: null,
    },
    {
      featureName: 'communication_tutor',
      frontendMeaning: 'Communication Tutor experience',
      backendMeaning: 'Display feature; pooled when live pool enabled',
      sameQuotaPool: true,
      safeToNormalizeWith: null,
    },
  ];
}
